import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Dropdown,
  Form,
  InputGroup,
  ListGroup
} from 'react-bootstrap';
import { MessageSquare, Plus, Search, Trash2, X } from 'lucide-react';
import Flex from '@/components/common/Flex';
import CancelModal from '@/app/common/components/CancelModal';
import DataLoadError from '@/components/errors/DataLoadError';
import { errorMessage, successMessage } from '@/utils/toast';
import {
  toMcpApiErrorMessage,
  useDeleteMcpThreadMutation
} from '../api/mcpApi';
import { useMcpThreadHistory } from '../hooks/useMcpThreadHistory';
import { invalidateMcpThreadHistory } from '../utils/threadHistoryEvents';
import { useStartNewMcpChat } from '../hooks/useStartNewMcpChat';
import { McpHistoryItemMenuToggle } from '../components/McpHistoryItemMenuToggle';
import { McpHistoryListPreload } from '../components/McpHistoryListPreload';
import {
  McpThreadHistoryIcon,
  McpThreadHistoryPresentation
} from '../components/McpThreadHistoryPresentation';
import { formatThreadTitleLine } from '../utils/threadHistoryDisplay';

type PendingDelete = { id: string; title: string };

function formatRelativeFa(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return 'همین الان';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} دقیقه پیش`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} ساعت پیش`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)} روز پیش`;

  return date.toLocaleDateString('fa-IR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

function formatAbsoluteFa(value: string): string {
  try {
    return new Date(value).toLocaleString('fa-IR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return value;
  }
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export default function McpHistoryPage() {
  const navigate = useNavigate();
  const startNewChat = useStartNewMcpChat();
  const [query, setQuery] = useState('');
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | false>(
    false
  );
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [deleteThread, { isLoading: isDeleting }] =
    useDeleteMcpThreadMutation();
  const debouncedQuery = useDebouncedValue(query, 300);

  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const {
    items,
    total,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    reload
  } = useMcpThreadHistory(true, debouncedQuery);

  useEffect(() => {
    if (!hasMore) return;
    const root = listRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          void loadMore();
        }
      },
      { root, rootMargin: '160px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, items.length]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteThread(pendingDelete.id).unwrap();
      invalidateMcpThreadHistory();
      successMessage('گفتگو با موفقیت حذف شد');
    } catch (err) {
      errorMessage(toMcpApiErrorMessage(err, 'حذف ناموفق بود'));
    }
  };

  const hasQuery = debouncedQuery.trim().length > 0;
  const resultLabel = useMemo(() => {
    if (loading && items.length === 0) return 'در حال بارگذاری…';
    if (hasQuery) {
      return total > 0
        ? `${total.toLocaleString('fa-IR')} نتیجه برای «${debouncedQuery.trim()}»`
        : 'نتیجه‌ای پیدا نشد';
    }
    return total > 0
      ? `${total.toLocaleString('fa-IR')} گفتگو`
      : 'هنوز گفتگویی ذخیره نشده';
  }, [debouncedQuery, hasQuery, items.length, loading, total]);

  return (
    <>
      <CancelModal
        show={Boolean(pendingDelete)}
        setShow={setPendingDelete}
        title="حذف گفتگو"
        submitTitle="آره، حذف بشه"
        isSubmitting={isDeleting}
        submitFunc={confirmDelete}
        buttonSize="sm"
        desc={
          <>
            آیا مطمئن هستید می‌خواهید گفتگوی{' '}
            <strong>
              {pendingDelete ? pendingDelete.title : ''}
            </strong>{' '}
            را حذف کنید؟
          </>
        }
      />

      {!loading && error ? (
        <DataLoadError
          className="mb-3"
          onRetry={() => void reload()}
          isRetrying={loading}
          isNetworkError={/failed to fetch|network|internet|اتصال/i.test(
            error
          )}
        />
      ) : (
      <Card className="mb-3">
      <Card.Header className="bg-body-tertiary border-bottom">
        <Flex
          alignItems="start"
          justifyContent="between"
          wrap="wrap"
          className="gap-3"
        >
          <div className="min-w-0">
            <h5 className="mb-1">سابقه گفتگوها</h5>
            <p className="mb-0 fs--1 text-600">
              گفتگوهای قبلی را پیدا کنید، ادامه دهید یا حذف کنید.
            </p>
          </div>
          <Button
            size="sm"
            variant="falcon-primary"
            className="flex-shrink-0"
            onClick={() => startNewChat()}
          >
            <Plus size={15} strokeWidth={2.25} className="me-1" />
            چت جدید
          </Button>
        </Flex>
      </Card.Header>

      <Card.Body className="border-bottom py-3">
        <Flex
          alignItems="center"
          justifyContent="between"
          wrap="wrap"
          className="gap-2"
        >
          <InputGroup className="flex-grow-1" style={{ maxWidth: 420 }}>
            <InputGroup.Text className="bg-body">
              <Search size={15} strokeWidth={2} className="text-500" />
            </InputGroup.Text>
            <Form.Control
              id="mcp-history-search"
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="جستجو در عنوان گفتگو…"
              autoComplete="off"
            />
            {query ? (
              <Button
                variant="outline-secondary"
                onClick={() => setQuery('')}
                aria-label="پاک کردن جستجو"
              >
                <X size={14} strokeWidth={2.25} />
              </Button>
            ) : null}
          </InputGroup>
          <div className="fs--2 text-500">{resultLabel}</div>
        </Flex>
      </Card.Body>

      <div
        ref={listRef}
        className="overflow-auto"
        style={{ maxHeight: 'calc(100vh - 18rem)', minHeight: 280 }}
      >
        {loading && items.length === 0 && <McpHistoryListPreload rows={8} />}

        {!loading && !error && items.length === 0 && (
          <div className="text-center py-5 px-3">
            <div className="d-inline-flex align-items-center justify-content-center text-500 mb-3">
              <MessageSquare size={22} strokeWidth={1.75} />
            </div>
            <h5 className="fs-0 mb-2">
              {hasQuery ? 'نتیجه‌ای پیدا نشد' : 'هنوز گفتگویی ندارید'}
            </h5>
            <p className="mb-3 fs--1 text-600 mx-auto" style={{ maxWidth: 320 }}>
              {hasQuery
                ? 'عبارت دیگری را امتحان کنید یا جستجو را پاک کنید.'
                : 'اولین گفتگو را شروع کنید؛ اینجا ذخیره می‌شود.'}
            </p>
            {!hasQuery && (
              <Button
                size="sm"
                variant="falcon-primary"
                onClick={() => startNewChat()}
              >
                <Plus size={15} strokeWidth={2.25} className="me-1" />
                شروع گفتگو
              </Button>
            )}
          </div>
        )}

        {items.length > 0 && (
          <ListGroup variant="flush">
            {items.map(item => {
              const title = formatThreadTitleLine(item.title);
              const messageCount = item.messageCount ?? 0;
              return (
                <ListGroup.Item
                  key={item.id}
                  className={classNames(
                    'px-3 px-md-4 py-3 border-200 position-relative mcp-history-page-row',
                    { 'is-menu-open': menuOpenId === item.id }
                  )}
                >
                  <Link
                    to={`/chat/c/${item.id}`}
                    className="d-flex align-items-start gap-3 min-w-0 pe-5 text-decoration-none text-body"
                  >
                    <McpThreadHistoryIcon
                      title={item.title}
                      size={17}
                      className="mt-1 flex-shrink-0"
                    />
                    <span className="min-w-0 flex-grow-1">
                      <McpThreadHistoryPresentation
                        title={item.title}
                        layout="page"
                      />
                      <span
                        className="d-flex flex-wrap align-items-center gap-2 mt-1 mb-0 fs--2 text-500 lh-sm"
                      >
                        <span
                          className="text-nowrap"
                          title={formatAbsoluteFa(item.updatedAt)}
                        >
                          {formatRelativeFa(item.updatedAt)}
                        </span>
                        <span className="text-400" aria-hidden>·</span>
                        <span className="text-nowrap">
                          {messageCount.toLocaleString('fa-IR')} پیام
                        </span>
                      </span>
                    </span>
                  </Link>
                  <div
                    className="position-absolute top-50 translate-middle-y end-0 me-2 z-1 d-flex align-items-center"
                  >
                    <Dropdown
                      align="end"
                      show={menuOpenId === item.id}
                      onToggle={next =>
                        setMenuOpenId(next ? item.id : null)
                      }
                    >
                      <Dropdown.Toggle as={McpHistoryItemMenuToggle} />
                      <Dropdown.Menu
                        className="mcp-history-item-menu border py-1 shadow-sm"
                      >
                        <Dropdown.Item
                          className="fs--1 text-danger d-flex align-items-center gap-2 py-2"
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            setMenuOpenId(null);
                            setPendingDelete({ id: item.id, title });
                          }}
                        >
                          <Trash2 size={14} strokeWidth={2} />
                          حذف
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}

        <div ref={sentinelRef} style={{ height: 1 }} />

        {loadingMore && <McpHistoryListPreload rows={3} />}
      </div>
    </Card>
      )}
    </>
  );
}
